import useProxy from '../Hooks/useProxy';

export default function API() {
  const { AUTH_SCHEME } = window.databeasy || {};
  const proxy = useProxy();

  function bufferToBase64url(buffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  return {
    login: (params, onSuccess, onFailure) =>
      proxy.run(
        'auth/login',
        'post',
        params,
        (...args) => {
          if (onSuccess) onSuccess(...args);
        },
        onFailure,
        {
          preventRedirectToLogin: true,
          headers: { 'x-auth-scheme': AUTH_SCHEME },
        }
      ),
    twoFactorLogin: (params, onSuccess, onFailure) =>
      proxy.run(
        'auth/twoFactorLogin',
        'post',
        params,
        (...args) => {
          if (onSuccess) onSuccess(...args);
        },
        onFailure,
        {
          preventRedirectToLogin: true,
          headers: { 'x-auth-scheme': AUTH_SCHEME },
        }
      ),
    logout: () => {
      return proxy.run('auth/logout', 'get', null, null, null, {
        preventRedirectToLogin: true,
      });
    },
    externalLogin: (provider, onSuccess, onFailure) =>
      proxy.run(
        `auth/externalLogin/${provider}`,
        'post',
        null,
        (...args) => {
           
          console.log(...args);
        },
        onFailure,
        { headers: { 'x-auth-scheme': AUTH_SCHEME } }
      ),
    getUserInfo: (onSuccess, onFailure) =>
      proxy.run('auth/userinfo', 'get', null, onSuccess, onFailure, {
        preventRedirectToLogin: true,
      }),
    getUserRoles: (onSuccess, onFailure) =>
      proxy.run('auth/userroles', 'get', null, onSuccess, onFailure),
    getAppVersion: (AppName, onSuccess, onFailure) =>
      proxy.run(
        `version/${encodeURIComponent(AppName)}`,
        'get',
        null,
        onSuccess,
        onFailure
      ),
    generateTotpSetup: (onSuccess, onFailure) =>
      proxy.run('auth/generate-totp-setup', 'get', null, onSuccess, onFailure, {
        preventRedirectToLogin: true,
      }),
    verifyTotp: (params, onSuccess, onFailure) =>
      proxy.run('auth/verify-totp', 'post', params, onSuccess, onFailure, {
        preventRedirectToLogin: true,
        headers: { 'x-auth-scheme': AUTH_SCHEME },
      }),
    twofactorEnabled: (onSuccess, onFailure) =>
      proxy.run(
        'auth/twofactor-enabled/authenticator',
        'get',
        null,
        onSuccess,
        onFailure,
        {
          preventRedirectToLogin: true,
        }
      ),
    disableTwoFactor: (onSuccess, onFailure) =>
      proxy.run('auth/disable-twofactor', 'post', null, onSuccess, onFailure, {
        preventRedirectToLogin: true,
        headers: { 'x-auth-scheme': AUTH_SCHEME },
      }),
    fido2Enabled: (onSuccess, onFailure) =>
      proxy.run(`auth/fido2-is-registered`, 'get', null, onSuccess, onFailure),
    fido2removecredentials: (onSuccess, onFailure) => {
      proxy.run(
        'auth/fido2-remove-credential',
        'delete',
        null,
        onSuccess,
        onFailure,
        {
          preventRedirectToLogin: true,
        }
      );
    },
    fido2register: (onSuccess, onFailure) => {
      proxy.run(
        'auth/fido2-registration-options',
        'post',
        null,
        async (opt) => {
          const options = { ...opt };
          options.challenge = Uint8Array.from(
            atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')),
            (c) => c.charCodeAt(0)
          ).buffer;
          options.user.id = Uint8Array.from(
            atob(options.user.id.replace(/-/g, '+').replace(/_/g, '/')),
            (c) => c.charCodeAt(0)
          ).buffer;

          const credential = await navigator.credentials.create({
            publicKey: options,
          });

          if (!credential) return;

          const attestationResponse = {
            id: credential.id,
            rawId: bufferToBase64url(credential.rawId),
            type: credential.type,
            response: {
              attestationObject: bufferToBase64url(
                credential.response.attestationObject
              ),
              clientDataJSON: bufferToBase64url(
                credential.response.clientDataJSON
              ),
            },
            clientExtensionResults:
              credential.getClientExtensionResults?.() || {},
          };

          await proxy.run(
            'auth/fido2-register',
            'post',
            JSON.stringify(attestationResponse),
            onSuccess,
            onFailure,
            {
              headers: {
                'Content-Type': 'application/json',
                'x-auth-scheme': AUTH_SCHEME,
              },
            }
          );
        },
        onFailure,
        {
          preventRedirectToLogin: true,
          headers: { 'x-auth-scheme': AUTH_SCHEME },
        }
      );
    },
    fido2Authenticate: (onSuccess, onFailure) => {
      proxy.run(
        'auth/fido2-authentication-options',
        'post',
        null,
        async (opt) => {
          const options = { ...opt };
          options.challenge = Uint8Array.from(
            atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')),
            (c) => c.charCodeAt(0)
          ).buffer;
          /*eslint no-param-reassign: "error"*/
          options.allowCredentials.forEach((cred) => {
            cred.id = Uint8Array.from(
              atob(cred.id.replace(/-/g, '+').replace(/_/g, '/')),
              (c) => c.charCodeAt(0)
            ).buffer;
          });

          const assertion = await navigator.credentials.get({
            publicKey: options,
          });

          const assertionResponse = {
            id: assertion.id,
            rawId: bufferToBase64url(assertion.rawId),
            response: {
              clientDataJSON: bufferToBase64url(
                assertion.response.clientDataJSON
              ),
              authenticatorData: bufferToBase64url(
                assertion.response.authenticatorData
              ),
              signature: bufferToBase64url(assertion.response.signature),
              userHandle: assertion.response.userHandle
                ? bufferToBase64url(assertion.response.userHandle)
                : null,
            },
            type: assertion.type,
          };

          await proxy.run(
            'auth/fido2-authenticate',
            'post',
            JSON.stringify(assertionResponse),
            onSuccess,
            onFailure,
            {
              headers: {
                'Content-Type': 'application/json',
                'x-auth-scheme': AUTH_SCHEME,
              },
            }
          );
        },
        onFailure,
        {
          preventRedirectToLogin: true,
          headers: { 'x-auth-scheme': AUTH_SCHEME },
        }
      );
    },
  };
}
